import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserDetails } from '../models/user-details';

export interface Settings {
    appRoutes: Array<AppRoute>;
    baseUrl: string;
    dataUrl: string;
    printServerUrl: string;
    fileServerUrl: string;
    loginUrl: string;
    loginUrlUserlink: string;
    loginUrlCredentials: string;
    canModifyTwoFaLogin: boolean;
    companyId: string;
    companyName: string;
    custNo: number;
    clientId: string;
    urlLogoWelcome: string;
    isAuthorized: boolean;
    clientApiProxyId: string;
    version: string;
    fileServerProxyId: string;
    printServerProxyId: string;
    user?: UserDetails;
    agGridLicense: string;
    clientSessionId: string;
    serverName?: string;
}

export interface AppRoute {
    key: number;
    id: string;
    path: string;
    component: string;
    routeId: string;
    order: number;
    remarks: string;
}

export interface ApiResult<T> {
    isError: boolean;
    message: string;
    data: T;
}

@Injectable({
    providedIn: 'root',  // This ensures that the service is a singleton
})
export class AppSettingsService {
    private _settings: Settings;
    private _isReady: boolean = false;
    private _clientSessionId: string = '';

    constructor(private http: HttpClient) {
        this._settings = {} as Settings;
    }

    get settings(): Settings {
        return this._settings;
    }

    set settings(value: Settings) {
        this._settings = value;
    }

    get clientSessionId(): string {
        return this._clientSessionId;
    }

    set clientSessionId(value: string) {
        this._clientSessionId = value;
    }

    public setIsReady() {
        this._isReady = true;
    }

    public async ensureIsReady(): Promise<void> {
        await this.waitForTrue(() => this._isReady);
    }

    public async triggerUserInfoCacheUpdate(): Promise<ApiResult<boolean>> {
        try {
            return await firstValueFrom(
                this.http.post<ApiResult<boolean>>(`/api/settings/TriggerUserInfoCacheUpdate`, {})
            );
        } catch (e: any) {
            return {
                isError: true,
                message: e?.message ?? 'Request failed',
                data: false,
            };
        }
    }

    public async triggerCompanyCacheUpdate(): Promise<ApiResult<boolean>> {
        try {
            return await firstValueFrom(
                this.http.post<ApiResult<boolean>>(`/api/settings/TriggerCompanyCacheUpdate`, {})
            );
        } catch (e: any) {
            return {
                isError: true,
                message: e?.message ?? 'Request failed',
                data: false,
            };
        }
    }

    private waitForTrue(variable: () => boolean, interval: number = 100): Promise<void> {
        return new Promise((resolve) => {
            const intervalId = setInterval(() => {
                if (variable()) {
                    clearInterval(intervalId);
                    resolve();
                }
            }, interval);
        });
    }
}
